import { memo, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MemberCard, Reveal, SectionHeading, Seo } from "@/components";
import { Container } from "@/layouts";
import { TEAM_DOMAIN_COLORS, type TeamDomain } from "@/constants/team";
import { TEAM_DOMAIN_ITEMS, TEAM_MEMBERS } from "@/data";
import { ROUTES, normalizeLanguage } from "@/navigations";
import type { TeamMember } from "@/types/domain";
import { breadcrumbJsonLd } from "@/utils/seo";

import shared from "@/styles/pages.module.css";
import styles from "./styles.module.css";

const TeamPage = memo(() => {
  const { t } = useTranslation("TeamPage");
  const { t: tSeo } = useTranslation("Seo");
  const { t: tc, i18n } = useTranslation("components");
  const lang = normalizeLanguage(i18n.language);
  const [filter, setFilter] = useState<TeamDomain | "all">("all");

  const leaders = useMemo(() => TEAM_MEMBERS.filter((m) => m.leadership), []);
  const coordinators = useMemo(() => TEAM_MEMBERS.filter((m) => m.coordinator), []);
  const groups = useMemo(
    () =>
      TEAM_DOMAIN_ITEMS.filter(({ id }) => filter === "all" || id === filter).map(({ id, icon, color }) => ({
        id,
        icon,
        color,
        lead: TEAM_MEMBERS.find((m) => m.groupLead && m.domain === id),
        members: TEAM_MEMBERS.filter((m) => !m.leadership && !m.coordinator && !m.groupLead && m.domain === id),
      })),
    [filter],
  );

  const renderCard = (m: TeamMember) => (
    <MemberCard
      key={m.id}
      name={m.name}
      role={t(`members.${m.id}.role`)}
      program={t(`members.${m.id}.program`)}
      focus={t(`members.${m.id}.focus`)}
      statement={t(`members.${m.id}.statement`)}
      accent={TEAM_DOMAIN_COLORS[m.domain]}
    />
  );

  return (
    <>
      <Seo
        title={tSeo("team.title")}
        description={tSeo("team.description")}
        path={ROUTES.TEAM}
        jsonLd={breadcrumbJsonLd(
          [
            { name: tc("nav.home"), path: ROUTES.HOME },
            { name: tc("nav.team"), path: ROUTES.TEAM },
          ],
          lang,
        )}
      />

      <section className={shared.pageHeader}>
        <Container>
          <Reveal className={shared.pageHeaderInner}>
            <span className="eyebrow" data-reveal>
              {t("hero.eyebrow")}
            </span>
            <h1 className={shared.pageTitle} data-reveal>
              {t("hero.title")}
            </h1>
            <p className={shared.lede} data-reveal>
              {t("hero.lead")}
            </p>
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.sunken}`}>
        <Container>
          <SectionHeading title={t("domains.title")} lead={t("domains.lead")} />
          <Reveal className={`${styles.domainGrid} ${shared.stackGap}`}>
            {TEAM_DOMAIN_ITEMS.map(({ id, icon: Icon, color }) => (
              <button
                key={id}
                type="button"
                className={`${styles.domainCard} ${filter === id ? styles.domainActive : ""}`}
                style={{ ["--team-color" as string]: color }}
                onClick={() => setFilter((prev) => (prev === id ? "all" : id))}
                data-reveal
              >
                <span className={styles.domainIcon}>
                  <Icon size={20} strokeWidth={1.6} />
                </span>
                <h3 className={styles.domainTitle}>{t(`domains.items.${id}.title`)}</h3>
                <p className={styles.domainBody}>{t(`domains.items.${id}.body`)}</p>
              </button>
            ))}
          </Reveal>
        </Container>
      </section>

      <section className={shared.section}>
        <Container>
          <SectionHeading title={t("leadership.title")} lead={t("leadership.lead")} />
          <Reveal className={`${styles.leaderGrid} ${shared.stackGap}`}>
            {leaders.map(renderCard)}
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.sunken}`}>
        <Container>
          <SectionHeading title={t("coordinator.title")} lead={t("coordinator.lead")} />
          <Reveal className={`${styles.leaderGrid} ${shared.stackGap}`}>
            {coordinators.map(renderCard)}
          </Reveal>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.atlas}`}>
        <Container>
          <SectionHeading title={t("groups.title")} lead={t("groups.lead")} />
          <div className={`${styles.groups} ${shared.stackGap}`}>
            {groups.map(({ id, icon: Icon, color, lead, members }) => (
              <Reveal key={id}>
                <div className={styles.group} style={{ ["--team-color" as string]: color }}>
                  <header className={styles.groupHead} data-reveal>
                    <span className={styles.groupIcon}>
                      <Icon size={18} strokeWidth={1.6} />
                    </span>
                    <h3 className={styles.groupTitle}>{t(`domains.items.${id}.title`)}</h3>
                  </header>
                  {lead ? (
                    <div className={styles.leadSlot} data-reveal>
                      <p className={styles.slotLabel}>{t("groups.leadLabel")}</p>
                      {renderCard(lead)}
                    </div>
                  ) : null}
                  <div data-reveal>
                    <p className={styles.slotLabel}>{t("groups.membersLabel")}</p>
                    {members.length > 0 ? (
                      <div className={styles.memberGrid}>{members.map(renderCard)}</div>
                    ) : (
                      <p className={styles.empty}>{t("groups.empty")}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className={`${shared.section} ${shared.invert}`}>
        <Container size="narrow">
          <Reveal>
            <p className={styles.note} data-reveal>
              {t("note.body")}
            </p>
          </Reveal>
        </Container>
      </section>
    </>
  );
});

TeamPage.displayName = "TeamPage";
export default TeamPage;
